import React, { useState, useEffect, useRef } from 'react';
import { Search, X, TrendingUp, Clock, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { getSearchTickers, searchStocks, hideNativeTabBar, showNativeTabBar } from '../../services/api';
import './SearchModal.css';

const SearchModal = ({ isOpen, onClose, onStockClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [tickersList, setTickersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);
  const [recentSearches, setRecentSearches] = useState([

  ]);
  const searchInputRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) hideNativeTabBar();
    else showNativeTabBar();
    return () => showNativeTabBar();
  }, [isOpen]);

  // 초기 티커 목록 로드
  useEffect(() => {
    const fetchInitialTickers = async () => {
      try {
        console.log('Fetching initial tickers...');
        setInitialLoading(true);
        const data = await getSearchTickers();
        console.log('Tickers fetched:', data);
        setTickersList(data.list || []);
      } catch (error) {
        console.error('Error fetching initial tickers:', error);
        // 에러 발생해도 페이지 새로고침 방지
      } finally {
        setInitialLoading(false);
        console.log('Tickers loading finished');
      }
    };

    if (isOpen) {
      // API 호출을 약간 지연시켜서 모달이 완전히 렌더링된 후 호출
      setTimeout(() => {
        fetchInitialTickers();
      }, 100);
    }
  }, [isOpen]);

  // 모달이 열릴 때 입력 필드에 포커스 (iOS 키보드 문제 해결)
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // iOS에서 키보드 문제를 방지하기 위해 지연된 포커스
      // 스프링 애니메이션 완료 후 포커스 (애니메이션 중 autoFocus 방지)
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 400);
    }
  }, [isOpen]);

  // iOS 키보드 관련 이벤트 처리
  useEffect(() => {
    if (!isOpen) return;

    const handleViewportChange = () => {
      // iOS에서 키보드가 올라올 때 뷰포트 변경 처리
      if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      }
    };

    const handleTouchMove = (e) => {
      // 모달 내부에서의 스크롤은 허용하되, 배경 스크롤은 방지
      const target = e.target.closest('.search-modal-body');
      if (!target) {
        e.preventDefault();
      }
    };

    // 이벤트 리스너 추가
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });

    // 초기 실행
    handleViewportChange();

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isOpen]);

  // 검색어 변경 시 API 호출 (디바운싱 적용)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim()) {
        handleSearch(searchTerm.trim());
      } else {
        setSearchResults([]);
        setHasSearched(false); // 검색어가 없으면 검색하지 않은 상태로 초기화
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleSearch = async (query) => {
    try {
      setLoading(true);
      setHasSearched(false); // 검색 시작시 초기화
      const data = await searchStocks(query);
      setSearchResults(data.list || []);
    } catch (error) {
      console.error('Error searching stocks:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
      setHasSearched(true); // 검색 완료 표시
    }
  };

  const handleStockSelect = (ticker) => {
    // Add to recent searches if not already present
    if (!recentSearches.includes(ticker)) {
      setRecentSearches(prev => [ticker, ...prev.slice(0, 4)]);
    }

    // 모달 닫기
    onClose();
    setSearchTerm('');
    setSearchResults([]);

    // 페이지 이동은 부모 컴포넌트에서 처리
    onStockClick(ticker);
  };

  // 주식 로고 이미지 경로 생성
  const getStockLogoPath = (ticker) => {
    return `/CompanyLogo/${ticker}.svg`;
  };

  // 이미지 로드 에러 처리
  const handleImageError = (e) => {
    e.target.style.display = 'none';
    const fallbackIcon = e.target.nextElementSibling;
    if (fallbackIcon && fallbackIcon.classList.contains('fallback-icon')) {
      fallbackIcon.style.display = 'block';
    }
  };

  const handleInputChange = (e) => {
    // input의 기본 동작(타이핑)은 막으면 안됨
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleKeyDown = (e) => {
    // Escape 키만 특별히 처리하고 나머지는 기본 동작 허용
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
    // Enter 키로 검색 결과 첫 번째 항목 선택하는 기능 추가
    if (e.key === 'Enter' && searchResults.length > 0) {
      e.preventDefault();
      handleStockSelect(searchResults[0].ticker);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="search-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <motion.div
            className="far-modal-root fixed inset-0 z-[10000] bg-white flex flex-col overflow-hidden"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
          >
        <div className="far-header">
          <div className="far-header-left">
            <span className="far-header-report-type">STOCK SEARCH</span>
            <form
              onSubmit={(e) => { e.preventDefault(); return false; }}
              className="search-input-container"
            >
              <Search className="search-icon" size={20} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="주식 심볼 또는 회사명 검색..."
                value={searchTerm}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className="search-input"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
              {searchTerm && (
                <button type="button" onClick={clearSearch} className="clear-button">
                  <X size={16} />
                </button>
              )}
            </form>
          </div>
          <button type="button" onClick={onClose} className="far-close-btn">
            <X size={15} />
          </button>
        </div>

        <div className="search-modal-body">
          {searchTerm.trim() ? (
            // 검색 결과 표시
            <div className="search-section">
              <div className="section-header">
                <h3 className="section-title">검색 결과</h3>
                {loading && <div className="search-loading">검색 중...</div>}
              </div>
              
              {loading ? (
                <div className="search-results">
                  {[...Array(5)].map((_, index) => (
                    <div key={index} className="search-result-item skeleton-loading">
                      <div className="stock-info">
                        <div className="stock-symbol skeleton"></div>
                        <div className="stock-name skeleton"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : searchResults.length > 0 ? (
                <div className="search-results">
                  {searchResults.map((stock) => (
                    <div
                      key={stock.ticker}
                      className="search-result-item"
                      onClick={() => handleStockSelect(stock.ticker)}
                    >
                      <div className="stock-icon">
                        <img 
                          src={getStockLogoPath(stock.ticker)} 
                          alt={stock.nameKr || stock.ticker}
                          onError={handleImageError}
                        />
                        <TrendingUp size={20} className="fallback-icon" />
                      </div>
                      <div className="stock-info">
                        <div className="stock-symbol">{stock.ticker}</div>
                        <div className="stock-name">
                          {stock.nameKr && <div>{stock.nameKr}</div>}
                          {stock.nameEn && <div>{stock.nameEn}</div>}
                        </div>
                        {stock.keyword && (
                          <div className="stock-keyword">{stock.keyword}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : !loading && hasSearched ? (
                <div className="no-results">
                  <div className="no-results-icon">
                    <Search size={48} />
                  </div>
                  <h3 className="no-results-title">검색 결과가 없습니다</h3>
                  <p className="no-results-description">
                    원하시는 주식 정보를 찾지 못하셨나요?<br />
                    커뮤니티에서 다른 투자자들에게 문의해보세요!
                  </p>
                </div>
              ) : null}
            </div>
          ) : (
            <>
              {recentSearches.length > 0 && (
                <div className="search-section">
                  <h3 className="section-title">
                    <Clock size={16} />
                    최근 검색
                  </h3>
                  <div className="recent-searches">
                    {recentSearches.map((symbol) => (
                      <button
                        key={symbol}
                        type="button"
                        className="recent-search-item"
                        onClick={() => handleStockSelect(symbol)}
                      >
                        {symbol}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="search-section">
                {initialLoading ? (
                  <div className="popular-stocks">
                    {[...Array(10)].map((_, index) => (
                      <div key={index} className="popular-stock-item skeleton-loading">
                        <div className="stock-icon skeleton"></div>
                        <div className="stock-info">
                          <div className="stock-symbol skeleton"></div>
                          <div className="stock-name skeleton"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="popular-stocks">
                    {tickersList.map((stock) => (
                      <div
                        key={stock.ticker}
                        className="popular-stock-item"
                        onClick={() => handleStockSelect(stock.ticker)}
                      >
                        <div className="stock-icon">
                          <img 
                            src={getStockLogoPath(stock.ticker)} 
                            alt={stock.nameKr || stock.ticker}
                            onError={handleImageError}
                          />
                          <TrendingUp size={20} className="fallback-icon" />
                        </div>
                        <div className="stock-info">
                          <div className="stock-symbol">{stock.ticker}</div>
                          <div className="stock-name">
                            {stock.nameKr && <div>{stock.nameKr}</div>}
                            {stock.nameEn && <div>{stock.nameEn}</div>}
                          </div>
                          {stock.keyword && (
                            <div className="stock-keyword">{stock.keyword}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SearchModal;